import React, { useState, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import "./QuantumCircuitSimulator.css"; // create the CSS file with content below
import QuantumAccessPopup from "../Quantum/QuantumAccessPopup";

/* ------------------------
   Low-level complex helpers
   ------------------------ */
const C_ZERO = { re: 0, im: 0 };
const C_ONE = { re: 1, im: 0 };

function cAdd(a, b) {
  return { re: a.re + b.re, im: (a.im || 0) + (b.im || 0) };
}
function cMulScalar(a, s) {
  return { re: a.re * s, im: a.im * s };
}
function cMul(a, b) {
  return {
    re: a.re * b.re - (a.im || 0) * (b.im || 0),
    im: a.re * (b.im || 0) + (a.im || 0) * b.re,
  };
}
function cConj(a) {
  return { re: a.re, im: -(a.im || 0) };
}
function cNorm2(a) {
  return a.re * a.re + (a.im || 0) * (a.im || 0);
}

/* ------------------------
   Standard single-qubit gates (complex numbers)
   Represented as 2x2 matrices of re/im objects or real numbers when possible
   ------------------------ */
const H = [
  [
    { re: 1 / Math.sqrt(2), im: 0 },
    { re: 1 / Math.sqrt(2), im: 0 },
  ],
  [
    { re: 1 / Math.sqrt(2), im: 0 },
    { re: -1 / Math.sqrt(2), im: 0 },
  ],
];

const X = [
  [
    { re: 0, im: 0 },
    { re: 1, im: 0 },
  ],
  [
    { re: 1, im: 0 },
    { re: 0, im: 0 },
  ],
];

const Y = [
  [
    { re: 0, im: 0 },
    { re: 0, im: -1 },
  ],
  [
    { re: 0, im: 1 },
    { re: 0, im: 0 },
  ],
];

const Z = [
  [
    { re: 1, im: 0 },
    { re: 0, im: 0 },
  ],
  [
    { re: 0, im: 0 },
    { re: -1, im: 0 },
  ],
];

const S = [
  [
    { re: 1, im: 0 },
    { re: 0, im: 0 },
  ],
  [
    { re: 0, im: 0 },
    { re: 0, im: 1 },
  ],
];

const T = [
  [
    { re: 1, im: 0 },
    { re: 0, im: 0 },
  ],
  [
    { re: Math.SQRT1_2, im: Math.SQRT1_2 },
    { re: 0, im: 0 },
  ],
];

/* ------------------------
   State helpers
   ------------------------ */
function zeroState(nQ) {
  const N = 1 << nQ;
  const st = Array.from({ length: N }, (_, i) =>
    i === 0 ? { re: 1, im: 0 } : { re: 0, im: 0 }
  );
  return st;
}

function measureProbabilities(state) {
  return state.map((a) => cNorm2(a));
}

/* ------------------------
   Apply a single-qubit gate (U is 2x2 complex matrix)
   state: array of complex amplitudes (length 2^n)
   nQ: number of qubits
   target: 0..nQ-1 (0 = most significant bit)
   ------------------------ */
function applySingleQubitGate(state, nQ, target, U) {
  const N = state.length;
  const out = Array.from({ length: N }, () => ({ re: 0, im: 0 }));
  const bitPos = nQ - target - 1;
  for (let idx = 0; idx < N; idx++) {
    const bit = (idx >> bitPos) & 1; // 0 or 1
    // j is index with target bit = 0 or 1 respectively
    // compute contribution to j from state[idx]
    for (let k = 0; k < 2; k++) {
      const j = (idx & ~(1 << bitPos)) | (k << bitPos);
      // multiply U[k][bit] * state[idx] (complex scalar * complex)
      const Ucell = U[k][bit];
      // Ucell may be real object {re,im} or number; assume object
      const prod = cMul(Ucell, state[idx]);
      out[j] = cAdd(out[j], prod);
    }
  }
  return out;
}

/* ------------------------
   Multi-qubit gates implemented on full state
   ------------------------ */

function applyCNOT(state, nQ, control, target) {
  const N = state.length;
  const out = Array.from({ length: N }, () => ({ re: 0, im: 0 }));
  const posC = nQ - control - 1;
  const posT = nQ - target - 1;
  for (let i = 0; i < N; i++) {
    const c = (i >> posC) & 1;
    if (c === 1) {
      const j = i ^ (1 << posT);
      out[j] = state[i];
    } else {
      out[i] = state[i];
    }
  }
  return out;
}

function applyCZ(state, nQ, control, target) {
  const N = state.length;
  const posC = nQ - control - 1;
  const posT = nQ - target - 1;
  return state.map((amp, idx) => {
    const c = (idx >> posC) & 1;
    const t = (idx >> posT) & 1;
    if (c === 1 && t === 1) {
      // multiply by -1
      return { re: -amp.re, im: -(amp.im || 0) };
    }
    return amp;
  });
}

function applySWAP(state, nQ, a, b) {
  const N = state.length;
  const posA = nQ - a - 1;
  const posB = nQ - b - 1;
  const out = Array.from({ length: N }, () => ({ re: 0, im: 0 }));
  for (let i = 0; i < N; i++) {
    const bitA = (i >> posA) & 1;
    const bitB = (i >> posB) & 1;
    let j = i;
    if (bitA !== bitB) {
      j = i ^ (1 << posA) ^ (1 << posB);
    }
    out[j] = state[i];
  }
  return out;
}

/* ------------------------
   Reduced single-qubit density matrix -> Bloch vector
   rho_{i,j} = sum_{rest} psi_{i,rest} * psi_{j,rest}^*
   returns [x,y,z] where
   x = 2*Re(rho01)
   y = -2*Im(rho01)
   z = rho00 - rho11
   ------------------------ */
function singleQubitBloch(state, nQ, qubit) {
  const N = state.length;
  const pos = nQ - qubit - 1;
  let rho00 = { re: 0, im: 0 };
  let rho01 = { re: 0, im: 0 };
  let rho11 = { re: 0, im: 0 };

  // iterate over all states, group by the value of that qubit
  for (let idx = 0; idx < N; idx++) {
    const bit = (idx >> pos) & 1;
    // build index for psi_{0,rest} and psi_{1,rest} contributions
    // For rho01 we need psi_{0,rest} * conj(psi_{1,rest})
    const amp = state[idx];
    if (bit === 0) {
      rho00 = cAdd(rho00, cMul(amp, cConj(amp))); // add |amp|^2 as complex same real
      // find partner index with that bit flipped to 1
      const partner = idx | (1 << pos);
      const amp1 = state[partner];
      const prod = cMul(amp, cConj(amp1));
      rho01 = cAdd(rho01, prod);
    } else {
      rho11 = cAdd(rho11, cMul(amp, cConj(amp)));
      // partner will be with bit 0
      const partner = idx & ~(1 << pos);
      const amp0 = state[partner];
      const prod = cMul(amp0, cConj(amp));
      // note: prod for rho01 already collected when partner was visited, so skipping to avoid double count
    }
  }

  const x = 2 * (rho01.re || 0);
  const y = -2 * (rho01.im || 0);
  const z = (rho00.re || 0) - (rho11.re || 0);
  return [x, y, z];
}

/* ------------------------
   Bloch arrow & canvas (re-usable)
   ------------------------ */
function BlochArrow({ vec }) {
  const arrowRef = useRef();
  const tipRef = useRef();
  useFrame(() => {
    if (!vec || !arrowRef.current) return;
    const mapped = new THREE.Vector3(vec[0], vec[2], -vec[1]).normalize();
    arrowRef.current.setDirection(mapped);
    arrowRef.current.setLength(1.1);
    if (tipRef.current) {
      tipRef.current.position.set(
        mapped.x * 1.1,
        mapped.y * 1.1,
        mapped.z * 1.1
      );
    }
  });
  return (
    <>
      <primitive
        ref={arrowRef}
        object={
          new THREE.ArrowHelper(
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, 0),
            1.1,
            0x00ff00
          )
        }
      />
      <mesh ref={tipRef}>
        <sphereGeometry args={[0.05, 16, 16]} />
        <meshBasicMaterial color="#ffff00" />
      </mesh>
    </>
  );
}

function BlochCanvas({ vec }) {
  if (!vec) return <div className="bloch-placeholder">—</div>;
  return (
    <Canvas
      style={{ width: 200, height: 200 }}
      camera={{ position: [0, 0, 1.7] }}
    >
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 5]} />
      <mesh>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial color="#0ff" wireframe />
      </mesh>
      <BlochArrow vec={vec} />
      <OrbitControls enableZoom={true} />
    </Canvas>
  );
}

/* ------------------------
   Component: QuantumCircuitSimulator
   ------------------------ */
export default function QuantumCircuitSimulator({ maxQubits = 15 }) {
  const [nQ, setNQ] = useState(4);
  const [state, setState] = useState(() => zeroState(4));
  const [queue, setQueue] = useState([]); // list of {gate, target, control}
  const [counts, setCounts] = useState(null); // for shot results
  const [lastPretty, setLastPretty] = useState("");
  const [shotsInput, setShotsInput] = useState(100);

  const [showPopup, setShowPopup] = useState(false);

  // reset when nQ changes
  const handleChangeN = (newN) => {
    setNQ(newN);
    setState(zeroState(newN));
    setQueue([]);
    setCounts(null);
    setLastPretty("");
  };

  const addToQueue = (g, t = 0, c = 0) => {
    setQueue((q) => [...q, { gate: g, target: Number(t), control: Number(c) }]);
  };

  const removeFromQueue = (idx) => {
    setQueue((q) => q.filter((_, i) => i !== idx));
  };

  const clearQueue = () => {
    setQueue([]);
  };

  const applySpec = (st, spec) => {
    const g = spec.gate;
    if (g === "H") return applySingleQubitGate(st, nQ, spec.target, H);
    if (g === "X") return applySingleQubitGate(st, nQ, spec.target, X);
    if (g === "Y") return applySingleQubitGate(st, nQ, spec.target, Y);
    if (g === "Z") return applySingleQubitGate(st, nQ, spec.target, Z);
    if (g === "S") return applySingleQubitGate(st, nQ, spec.target, S);
    if (g === "T") return applySingleQubitGate(st, nQ, spec.target, T);
    if (g === "CNOT") return applyCNOT(st, nQ, spec.control, spec.target);
    if (g === "CZ") return applyCZ(st, nQ, spec.control, spec.target);
    if (g === "SWAP") return applySWAP(st, nQ, spec.control, spec.target);
    return st;
  };

  // helper to convert state to a readable ket string (dominant terms)
  function prettyKet(st) {
    const terms = [];
    const N = st.length;
    for (let i = 0; i < N; i++) {
      const p = cNorm2(st[i]);
      if (p > 1e-4) terms.push({ idx: i, p });
    }
    terms.sort((a, b) => b.p - a.p);
    if (terms.length === 0) return `|${"0".repeat(nQ)}〉`;
    const out = terms
      .slice(0, 4)
      .map((t) => {
        const amp = Math.sqrt(t.p);
        return `${amp.toFixed(2)}|${t.idx.toString(2).padStart(nQ, "0")}〉`;
      })
      .join(" + ");
    return out;
  }

  // Run the queued circuit once (deterministic)
  const runOnce = () => {
    let s = state;
    for (const spec of queue) {
      s = applySpec(s, spec);
    }
    setState(s);
    setCounts(null);
    const pretty = prettyKet(s);
    setLastPretty(pretty);
  };

  // Run circuit and simulate "shots" (sampling using final probabilities)
  const runShots = (shots) => {
    let s = state;
    for (const spec of queue) s = applySpec(s, spec);
    const probs = measureProbabilities(s);
    const N = probs.length;
    const cnts = Array(N).fill(0);
    for (let k = 0; k < shots; k++) {
      const r = Math.random();
      let acc = 0;
      for (let i = 0; i < N; i++) {
        acc += probs[i];
        if (r <= acc) {
          cnts[i]++;
          break;
        }
      }
    }
    setCounts(cnts);
    setLastPretty(prettyKet(s));
  };

  // quick apply single gate to current state (like QubitPanel behavior)
  const quickApply = (g, target = 0, control = 0) => {
    const sNew = applySpec(state, { gate: g, target, control });
    setState(sNew);
    setCounts(null);
    setLastPretty(prettyKet(sNew));
  };

  // Bloch vectors memoized
  const blochVecs = useMemo(() => {
    const arr = [];
    for (let q = 0; q < nQ; q++) {
      arr.push(singleQubitBloch(state, nQ, q));
    }
    return arr;
  }, [state, nQ]);

  const probs = useMemo(() => measureProbabilities(state), [state]);

  return (
    <div className="qc-sim-root">
      {/*       
      <div className="qc-header-wrapper">
        <div className="qc-header">
          <h2>Quantum Circuit Builder</h2>
          <p className="qc-description">
            Design, Visualize and Run Quantum Circuits (up to 10 qubits)
          </p>
        </div>
        <hr className="qc-divider" />
      </div> */}

      {/* ROW 1: gate buttons + simple selectors */}
      <div className="qc-row qc-controls">
        <div className="qc-left"></div>

        <div className="qc-gates">
          <label
            className="lab-btn"
            onClick={() => {
              const dataStr = JSON.stringify(queue, null, 2);
              const blob = new Blob([dataStr], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "quantum_circuit.json";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Export JSON
          </label>

          <label className="lab-btn" style={{ cursor: "pointer" }}>
            Import JSON
            <input
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  try {
                    const imported = JSON.parse(ev.target.result);
                    if (!Array.isArray(imported))
                      throw new Error("Invalid format");
                    // simple validation
                    for (const item of imported) {
                      if (!item.gate || item.target === undefined)
                        throw new Error("Missing fields");
                    }
                    setQueue(imported);
                  } catch (err) {
                    alert("Invalid JSON file: " + err.message);
                  }
                };
                reader.readAsText(file);
                e.target.value = null; // reset
              }}
            />
          </label>
        </div>
      </div>

      <div className="qc-row qc-probs visual">
        {/* ---------- Row 1: Builder ---------- */}
        <div className="builder-section">
          <h2
            style={{
              marginTop: 5,
              display: "inline-block",
              borderBottom: "2px solid #0ff", // neon green line
              paddingBottom: 4,
            }}
          >
            Circuit Builder
          </h2>
          <div className="builder-row">
            <div className="qc-left">
              <label>
                Qubits:
                <select
                  value={nQ}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (val > 5) {
                      setShowPopup(true); // show the hacker popup
                    } else {
                      handleChangeN(val); // ✅ use your own setter
                    }
                  }}
                >
                  {Array.from({ length: maxQubits - 2 }, (_, i) => i + 3).map(
                    (n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    )
                  )}
                </select>
              </label>
            </div>

            {showPopup && (
              <QuantumAccessPopup onClose={() => setShowPopup(false)} />
            )}

            <label>
              Gate
              <select id="builderGate" defaultValue="H">
                <option value="H">H</option>
                <option value="X">X</option>
                <option value="Y">Y</option>
                <option value="Z">Z</option>
                <option value="S">S</option>
                <option value="T">T</option>
                <option value="CNOT">CNOT</option>
                <option value="CZ">CZ</option>
                <option value="SWAP">SWAP</option>
              </select>
            </label>

            <label>
              Control
              <select id="builderControl">
                {Array.from({ length: nQ }, (_, i) => (
                  <option key={i} value={i}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Target
              <select id="builderTarget">
                {Array.from({ length: nQ }, (_, i) => (
                  <option key={i} value={i}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="builder-buttons">
            <button
              className="lab-btn"
              onClick={() => {
                const gate = document.getElementById("builderGate").value;
                const control = Number(
                  document.getElementById("builderControl").value
                );
                const target = Number(
                  document.getElementById("builderTarget").value
                );
                if (
                  (gate === "CNOT" || gate === "CZ" || gate === "SWAP") &&
                  control === target
                ) {
                  alert("Control and target must be different for this gate.");
                  return;
                }
                addToQueue(gate, target, control);
              }}
            >
              Add Gate
            </button>

            <button className="lab-btn" onClick={clearQueue}>
              Clear All
            </button>
            <button className="lab-btn" onClick={runOnce}>
              Run Once
            </button>
          </div>
        </div>

        <hr className="qc-divider" />

        {/* ---------- Row 2: Timeline / Circuit view ---------- */}
        <div className="timeline-section">
          <h2
            style={{
              marginTop: -15,
              display: "inline-block",
              borderBottom: "2px solid #0ff", // neon green line
              paddingBottom: 4,
            }}
          >
            Circuit Timeline
          </h2>
          <div className="timeline">
            {queue.length === 0 && (
              <div className="muted">
                No gates queued. Add gates from Builder.
              </div>
            )}
            {queue.map((spec, i) => (
              <div key={i} className="timeline-item">
                <div className="box">
                  <div className="gname">{spec.gate}</div>
                  <div className="gmeta">
                    {spec.control != null ? `c${spec.control + 1}` : ""} → t
                    {spec.target + 1}
                  </div>
                </div>
                <button
                  className="remove-btn"
                  onClick={() => removeFromQueue(i)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="shots-row">
            <label>
              Shots:{" "}
              <input
                type="number"
                value={shotsInput}
                onChange={(e) => setShotsInput(Number(e.target.value || 1))}
                min={1}
                max={10000}
              />
            </label>
            <button
              className="lab-btn"
              onClick={() => runShots(Number(shotsInput || 1))}
            >
              Run Shots
            </button>
          </div>
        </div>
      </div>

      {/* ROW 4: Bloch spheres (one per qubit) */}
      <div className="qc-row qc-blochs">
        <div className="bloch-grid">
          {blochVecs.map((v, i) => (
            <div className="bloch-card visual" key={i}>
              <h5>Qubit {i + 1}</h5>
              <BlochCanvas vec={v} bloch={v} />
              <div className="ket small">
                {lastPretty || `|${Array(nQ).fill("0").join("")}〉`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ROW 3: basis state probability bars */}
      <div className="qc-row qc-probs visual">
        <h4>Basis Probabilities</h4>
        <div className="probs-row">
          {probs.map((p, idx) => (
            <div className="prob" key={idx}>
              <div
                className="bar"
                style={{ height: `${Math.round(p * 100)}%` }}
              />
              <div className="ket">|{idx.toString(2).padStart(nQ, "0")}〉</div>
              <div className="pnum">{(p * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>

        {/* shot counts if present */}
        {counts && (
          <div className="counts">
            <h4>Measurement Counts</h4>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>Counts</th>
                </tr>
              </thead>
              <tbody>
                {counts.map((c, i) => (
                  <tr key={i}>
                    <td>|{i.toString(2).padStart(nQ, "0")}〉</td>
                    <td>{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ height: 20 }} />
    </div>
  );
}

/* ------------------------
   Helpers for JSX BlochCanvas binding
   ------------------------ */
/* small wrapper so props match */
function BlochCanvasWrapper({ bloch }) {
  return <BlochCanvas vec={bloch} />;
}
